import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceReportGenerator = ({ className = '' }) => {
  const [reportTemplates, setReportTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [reportParameters, setReportParameters] = useState({});
  const [generationStatus, setGenerationStatus] = useState('idle');
  const [recentReports, setRecentReports] = useState([]);

  useEffect(() => {
    const mockTemplates = [
      {
        id: 'gmp-compliance',
        name: 'GMP Compliance Report',
        description: 'Comprehensive Good Manufacturing Practice compliance assessment',
        authority: 'EMA',
        format: 'PDF',
        sections: ['Executive Summary', 'Quality System', 'Personnel', 'Premises & Equipment', 'Documentation', 'Production', 'Quality Control'],
        estimatedTime: '15-20 minutes',
        lastUsed: new Date('2024-09-15T10:30:00'),
        parameters: [
          { name: 'reportPeriod', label: 'Reporting Period', type: 'dateRange', required: true },
          { name: 'facility', label: 'Manufacturing Facility', type: 'select', options: ['Main Facility', 'Secondary Facility', 'All Facilities'], required: true },
          { name: 'includeAudits', label: 'Include Audit Findings', type: 'checkbox', default: true },
          { name: 'detailLevel', label: 'Detail Level', type: 'select', options: ['Summary', 'Detailed', 'Comprehensive'], required: true }
        ]
      },
      {
        id: 'regulatory-submission',
        name: 'Regulatory Submission Package',
        description: 'Complete submission package for marketing authorization',
        authority: 'EMA/MHRA',
        format: 'PDF + XML',
        sections: ['Administrative Information', 'Quality Documentation', 'Non-clinical Data', 'Clinical Data', 'Risk Management'],
        estimatedTime: '45-60 minutes',
        lastUsed: new Date('2024-09-10T14:15:00'),
        parameters: [
          { name: 'productName', label: 'Product Name', type: 'text', required: true },
          { name: 'submissionType', label: 'Submission Type', type: 'select', options: ['Initial Application', 'Variation', 'Renewal'], required: true },
          { name: 'targetAuthority', label: 'Target Authority', type: 'select', options: ['EMA', 'MHRA', 'BfArM', 'ANSM'], required: true },
          { name: 'includeQuality', label: 'Include Quality Module', type: 'checkbox', default: true },
          { name: 'includeClinical', label: 'Include Clinical Module', type: 'checkbox', default: true }
        ]
      },
      {
        id: 'audit-trail',
        name: 'Audit Trail Report',
        description: 'Blockchain-verified audit trail documentation',
        authority: 'Internal/External Auditors',
        format: 'PDF + CSV',
        sections: ['Audit Scope', 'System Activities', 'User Actions', 'Data Integrity', 'Blockchain Verification'],
        estimatedTime: '10-15 minutes',
        lastUsed: new Date('2024-09-18T16:45:00'),
        parameters: [
          { name: 'auditPeriod', label: 'Audit Period', type: 'dateRange', required: true },
          { name: 'systemScope', label: 'System Scope', type: 'multiselect', options: ['Quality Management', 'Laboratory', 'Manufacturing', 'Distribution'], required: true },
          { name: 'userFilter', label: 'User Filter', type: 'select', options: ['All Users', 'Specific Role', 'Specific User'], required: false },
          { name: 'includeBlockchain', label: 'Include Blockchain Verification', type: 'checkbox', default: true }
        ]
      },
      {
        id: 'pharmacovigilance',
        name: 'Pharmacovigilance Report',
        description: 'Adverse event reporting and safety analysis',
        authority: 'EMA/National Authorities',
        format: 'PDF + E2B XML',
        sections: ['Safety Overview', 'Adverse Events', 'Risk Assessment', 'Signal Detection', 'Benefit-Risk Analysis'],
        estimatedTime: '25-30 minutes',
        lastUsed: new Date('2024-09-12T11:20:00'),
        parameters: [
          { name: 'reportingPeriod', label: 'Reporting Period', type: 'dateRange', required: true },
          { name: 'productScope', label: 'Product Scope', type: 'multiselect', options: ['Cardiomax 50mg', 'Neurofen Plus', 'Diabetrol XR', 'All Products'], required: true },
          { name: 'severityFilter', label: 'Severity Filter', type: 'select', options: ['All Severities', 'Serious Only', 'Non-serious Only'], required: false },
          { name: 'includeSignals', label: 'Include Signal Detection', type: 'checkbox', default: true }
        ]
      }
    ];

    const mockRecentReports = [
      {
        id: 'RPT-2024-001',
        name: 'GMP Compliance Report - Q3 2024',
        template: 'GMP Compliance Report',
        generatedDate: new Date('2024-09-15T10:30:00'),
        generatedBy: 'Dr. Elena Vasquez',
        status: 'completed',
        fileSize: '2.4 MB',
        downloadCount: 3
      },
      {
        id: 'RPT-2024-002',
        name: 'Audit Trail Report - September 2024',
        template: 'Audit Trail Report',
        generatedDate: new Date('2024-09-18T16:45:00'),
        generatedBy: 'Dr. Sarah Chen',
        status: 'completed',
        fileSize: '1.8 MB',
        downloadCount: 1
      },
      {
        id: 'RPT-2024-003',
        name: 'Pharmacovigilance Report - Cardiomax 50mg',
        template: 'Pharmacovigilance Report',
        generatedDate: new Date('2024-09-12T11:20:00'),
        generatedBy: 'Dr. Lisa Thompson',
        status: 'completed',
        fileSize: '3.1 MB',
        downloadCount: 5
      }
    ];

    setReportTemplates(mockTemplates);
    setRecentReports(mockRecentReports);
  }, []);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    const initialParams = {};
    template?.parameters?.forEach(param => {
      if (param?.default !== undefined) {
        initialParams[param.name] = param?.default;
      }
    });
    setReportParameters(initialParams);
  };

  const handleParameterChange = (paramName, value) => {
    setReportParameters(prev => ({
      ...prev,
      [paramName]: value
    }));
  };

  const handleGenerateReport = async () => {
    setGenerationStatus('generating');
    
    // Simulate report generation
    setTimeout(() => {
      setGenerationStatus('completed');
      
      // Add to recent reports
      const newReport = {
        id: `RPT-2024-${String(recentReports?.length + 4)?.padStart(3, '0')}`,
        name: `${selectedTemplate?.name} - ${new Date()?.toLocaleDateString('en-GB')}`,
        template: selectedTemplate?.name,
        generatedDate: new Date(),
        generatedBy: 'Dr. Sarah Chen',
        status: 'completed',
        fileSize: `${(Math.random() * 3 + 1)?.toFixed(1)} MB`,
        downloadCount: 0
      };
      
      setRecentReports(prev => [newReport, ...prev]);
      
      setTimeout(() => {
        setGenerationStatus('idle');
        setSelectedTemplate(null);
        setReportParameters({});
      }, 2000);
    }, 3000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'generating': return 'text-warning';
      case 'failed': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'generating': return 'Clock';
      case 'failed': return 'AlertCircle';
      default: return 'Circle';
    }
  };

  const renderParameterInput = (param) => {
    switch (param?.type) {
      case 'text':
        return (
          <input
            type="text"
            value={reportParameters?.[param?.name] || ''}
            onChange={(e) => handleParameterChange(param?.name, e?.target?.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
            placeholder={param?.label}
            required={param?.required}
          />
        );
      
      case 'select':
        return (
          <select
            value={reportParameters?.[param?.name] || ''}
            onChange={(e) => handleParameterChange(param?.name, e?.target?.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
            required={param?.required}
          >
            <option value="">Select {param?.label}</option>
            {param?.options?.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'checkbox':
        return (
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={reportParameters?.[param?.name] || false}
              onChange={(e) => handleParameterChange(param?.name, e?.target?.checked)}
              className="rounded border-border"
            />
            <span className="text-sm text-foreground">{param?.label}</span>
          </label>
        );
      
      case 'dateRange':
        return (
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              value={reportParameters?.[`${param?.name}_start`] || ''}
              onChange={(e) => handleParameterChange(`${param?.name}_start`, e?.target?.value)}
              className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
              required={param?.required}
            />
            <input
              type="date"
              value={reportParameters?.[`${param?.name}_end`] || ''}
              onChange={(e) => handleParameterChange(`${param?.name}_end`, e?.target?.value)}
              className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
              required={param?.required}
            />
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-foreground">Compliance Report Generator</h2>
        <p className="text-sm text-muted-foreground">Generate regulatory reports and submissions</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Report Templates */}
        <div className="lg:col-span-2 space-y-4">
          <h3 className="text-lg font-medium text-foreground">Report Templates</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reportTemplates?.map((template) => (
              <div
                key={template?.id}
                className={`p-4 border rounded-lg cursor-pointer transition-all duration-150 ${
                  selectedTemplate?.id === template?.id
                    ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50'
                }`}
                onClick={() => handleTemplateSelect(template)}
              >
                <div className="flex items-start justify-between mb-3">
                  <h4 className="text-sm font-medium text-card-foreground">{template?.name}</h4>
                  <span className="px-2 py-1 text-xs font-medium bg-secondary/10 text-secondary rounded-full">
                    {template?.format}
                  </span>
                </div>
                
                <p className="text-xs text-muted-foreground mb-3">{template?.description}</p>
                
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Authority:</span>
                    <span className="text-card-foreground">{template?.authority}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Est. Time:</span>
                    <span className="text-card-foreground">{template?.estimatedTime}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Sections:</span>
                    <span className="text-card-foreground">{template?.sections?.length}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Report Configuration */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Report Configuration</h3>
          
          {selectedTemplate ? (
            <div className="bg-card border border-border rounded-lg p-4 space-y-4">
              <div>
                <h4 className="text-sm font-medium text-card-foreground mb-2">{selectedTemplate?.name}</h4>
                <p className="text-xs text-muted-foreground">{selectedTemplate?.description}</p>
              </div>
              
              <div className="space-y-3">
                {selectedTemplate?.parameters?.map((param) => (
                  <div key={param?.name}>
                    <label className="block text-xs font-medium text-card-foreground mb-1">
                      {param?.label}
                      {param?.required && <span className="text-error ml-1">*</span>}
                    </label>
                    {renderParameterInput(param)}
                  </div>
                ))}
              </div>
              
              <div className="pt-3 border-t border-border">
                <Button
                  variant="default"
                  className="w-full"
                  onClick={handleGenerateReport}
                  disabled={generationStatus === 'generating'}
                  loading={generationStatus === 'generating'}
                >
                  {generationStatus === 'generating' ? 'Generating Report...' : 'Generate Report'}
                </Button>
              </div>
              
              {generationStatus === 'completed' && (
                <div className="flex items-center space-x-2 p-3 bg-success/5 rounded-lg">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm text-success font-medium">Report generated successfully!</span>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <Icon name="FileText" size={32} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Select a report template to configure parameters</p>
            </div>
          )}
        </div>
      </div>
      {/* Recent Reports */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Recent Reports</h3>
        
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Report Name</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Template</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Generated</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Size</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentReports?.map((report) => (
                  <tr key={report?.id} className="border-b border-border hover:bg-muted/20 transition-colors duration-150">
                    <td className="p-4">
                      <div className="text-sm font-medium text-card-foreground">{report?.name}</div>
                      <div className="text-xs text-muted-foreground">{report?.id}</div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-card-foreground">{report?.template}</span>
                    </td>
                    <td className="p-4">
                      <div className="text-sm text-card-foreground">
                        {report?.generatedDate?.toLocaleDateString('en-GB')}
                      </div>
                      <div className="text-xs text-muted-foreground">by {report?.generatedBy}</div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-1">
                        <Icon name={getStatusIcon(report?.status)} size={14} className={getStatusColor(report?.status)} />
                        <span className={`text-sm font-medium ${getStatusColor(report?.status)}`}>
                          {report?.status?.toUpperCase()}
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-card-foreground">{report?.fileSize}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-1">
                        <Button variant="ghost" size="xs">
                          <Icon name="Download" size={14} />
                        </Button>
                        <Button variant="ghost" size="xs">
                          <Icon name="Eye" size={14} />
                        </Button>
                        <Button variant="ghost" size="xs">
                          <Icon name="Share" size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceReportGenerator;